import React, { useRef } from 'react';
import { Form } from 'formik-antd';
import { Formik } from 'formik';
import { Button, Divider, Modal } from 'antd';
import * as Yup from 'yup';

import SubmitButton from 'components/formik/SubmitButton';
import QuestionnaireElement from '../QuestionnaireElement';
import { AssignmentDetailsModel } from 'utils/assignmentUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseAlert from 'components/wrappers/BaseAlert';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { AssignmentQuestionnaireInfoModel } from 'api/backofficeApiClient';
import { AssignmentAdminDocumentModel } from 'api/adminApiClient';
import {
  saveClassificationQuestionnaire,
  updateClassificationQuestionnaire,
} from 'redux/reducers/assignmentSlice';
import {
  Questionnaire,
  mapQuestionnaireToRequest,
  mapQuestionnaireToInitialValues,
} from '../utils/mapUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  onClose: () => void;
  isAdmin?: boolean;
  isEditMode?: boolean;
  assignmentKey: string;
  assignmentId: string;
  questionnaireInfo?: AssignmentQuestionnaireInfoModel;
  setPrimaryDetails: (data: AssignmentDetailsModel) => void;
  setAdditionalDetails?: (data: AssignmentAdminDocumentModel) => void;
};

interface FormValues {
  ongoingTrainingRequired: Questionnaire;
  customerControl: Questionnaire;
  clockInOrOutDictated: Questionnaire;
  supervisionOfEmployee: Questionnaire;
  suppliesProvision: Questionnaire;
  businessExpensesReimbursed: Questionnaire;
  servicesAreKeyAspectOfBM: Questionnaire;
  specializedSkillsetEmployeeAvailbility: Questionnaire;
  workerPerformedSimilarServices: Questionnaire;
  areServicesAdvertised: Questionnaire;
}

const elementValidationSchema = Yup.object({
  closedQuestion: Yup.boolean().notRequired(),
  comment: Yup.string()
    .trim()
    .max(2000, 'Comment should be 2000 characters maximum'),
});

const validationSchema = Yup.object({
  ongoingTrainingRequired: elementValidationSchema,
  customerControl: elementValidationSchema,
  clockInOrOutDictated: elementValidationSchema,
  supervisionOfEmployee: elementValidationSchema,
  suppliesProvision: elementValidationSchema,
  businessExpensesReimbursed: elementValidationSchema,
  servicesAreKeyAspectOfBM: elementValidationSchema,
  specializedSkillsetEmployeeAvailbility: elementValidationSchema,
  workerPerformedSimilarServices: elementValidationSchema,
  areServicesAdvertised: elementValidationSchema,
});

export default function ClassificationQuestionnaireModal(props: Props) {
  const {
    onClose,
    assignmentId,
    assignmentKey,
    isAdmin,
    setPrimaryDetails,
    setAdditionalDetails,
    questionnaireInfo,
    isEditMode,
  } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const initialValues: FormValues = {
    ongoingTrainingRequired: mapQuestionnaireToInitialValues(
      questionnaireInfo?.ongoingTrainingRequired,
    ),
    customerControl: mapQuestionnaireToInitialValues(
      questionnaireInfo?.customerControl,
    ),
    clockInOrOutDictated: mapQuestionnaireToInitialValues(
      questionnaireInfo?.clockInOrOutDictated,
    ),
    supervisionOfEmployee: mapQuestionnaireToInitialValues(
      questionnaireInfo?.supervisionOfWorker,
    ),
    suppliesProvision: mapQuestionnaireToInitialValues(
      questionnaireInfo?.suppliesProvision,
    ),
    businessExpensesReimbursed: mapQuestionnaireToInitialValues(
      questionnaireInfo?.businessExpensesReimbursed,
    ),
    servicesAreKeyAspectOfBM: mapQuestionnaireToInitialValues(
      questionnaireInfo?.servicesAreKeyAspectOfBM,
    ),
    specializedSkillsetEmployeeAvailbility: mapQuestionnaireToInitialValues(
      questionnaireInfo?.specializedSkillsetEmployeeAvailbility,
    ),
    workerPerformedSimilarServices: mapQuestionnaireToInitialValues(
      questionnaireInfo?.workerPerformedSimilarServices,
    ),
    areServicesAdvertised: mapQuestionnaireToInitialValues(
      questionnaireInfo?.servicesAdvertisedByWorker,
    ),
  };

  const handleSubmit = async (values: FormValues) => {
    const {
      ongoingTrainingRequired,
      customerControl,
      clockInOrOutDictated,
      supervisionOfEmployee,
      suppliesProvision,
      businessExpensesReimbursed,
      specializedSkillsetEmployeeAvailbility,
      servicesAreKeyAspectOfBM,
      workerPerformedSimilarServices,
      areServicesAdvertised,
    } = values;

    const data: AssignmentQuestionnaireInfoModel = {
      ongoingTrainingRequired: mapQuestionnaireToRequest(
        ongoingTrainingRequired,
      ),
      customerControl: mapQuestionnaireToRequest(customerControl),
      clockInOrOutDictated: mapQuestionnaireToRequest(clockInOrOutDictated),
      supervisionOfWorker: mapQuestionnaireToRequest(supervisionOfEmployee),
      suppliesProvision: mapQuestionnaireToRequest(suppliesProvision),
      businessExpensesReimbursed: mapQuestionnaireToRequest(
        businessExpensesReimbursed,
      ),
      specializedSkillsetEmployeeAvailbility: mapQuestionnaireToRequest(
        specializedSkillsetEmployeeAvailbility,
      ),
      servicesAreKeyAspectOfBM: mapQuestionnaireToRequest(
        servicesAreKeyAspectOfBM,
      ),
      workerPerformedSimilarServices: mapQuestionnaireToRequest(
        workerPerformedSimilarServices,
      ),
      servicesAdvertisedByWorker: mapQuestionnaireToRequest(
        areServicesAdvertised,
      ),
    };

    const correctAction =
      isAdmin && isEditMode
        ? updateClassificationQuestionnaire
        : saveClassificationQuestionnaire;

    const result = await dispatch(
      correctAction({
        isAdmin,
        assignmentId,
        assignmentKey,
        quesitonnaireData: data,
      }),
    );

    if (correctAction.fulfilled.match(result)) {
      setPrimaryDetails(result.payload.primaryInfo);
      result.payload.additionalInfo &&
        setAdditionalDetails &&
        setAdditionalDetails(result.payload.additionalInfo);
      BaseAlert.success('Classification questionnaire saved.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title={
        isEditMode
          ? 'Edit classification questionnaire'
          : 'Classification questionnaire'
      }
      open={true}
      closable={true}
      maxWidth={888}
      onCancel={handleCloseModal}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <QuestionnaireElement
              fieldPrefix="ongoingTrainingRequired"
              label="With the exception of initial project orientation, does the customer require you to undergo ongoing training?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="customerControl"
              label="Does the customer control or have the right to control what you do and how you do your job?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="clockInOrOutDictated"
              label="Will the customer dictate when you should clock in or out?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="supervisionOfEmployee"
              label="Will you supervise any of the customer's employees?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="suppliesProvision"
              label="With the exception of equipment issued for the purpose of security, will the customer be providing supplies to you?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="businessExpensesReimbursed"
              label="Does the customer reimburse you for business expenses (office rent, commercial insurance, etc.)?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="servicesAreKeyAspectOfBM"
              label="Are you performing a specialized skillset currently not available through their existing employees?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="specializedSkillsetEmployeeAvailbility"
              label="Are the services performed a key aspect of the customer's regular business model?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="workerPerformedSimilarServices"
              label="Have you performed similar services to other clients previously?"
            />

            <Divider className="modal-divider" />

            <QuestionnaireElement
              fieldPrefix="areServicesAdvertised"
              label="Do you advertise your services?"
            />
          </div>

          <BaseModalFooter>
            <div>
              <Button
                data-id="closeClassificationQuestionnaireModalBtn"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>

              <SubmitButton
                type="primary"
                data-id="submitClassificationQuestionnaireBtn"
              >
                Save
              </SubmitButton>
            </div>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
